/** Kúsky, ktoré potrebuje viac kariet naraz. */

import { neighbors } from '../map.js';
import { freeSpacesInZonesOf, pathsWithin } from '../movement.js';
import type { Through } from '../movement.js';
import type { Ctx } from '../ops.js';
import { askChoice, emit, getMap, heroOf, tokensOf } from '../ops.js';
import { areEnemies } from '../players.js';
import type { FighterId, FollowUp, PlayerIndex, SpaceId } from '../types.js';
import type { EffectCtx } from './types.js';

export function fighterOrNull(ctx: Ctx, id: FighterId | null) {
  return id ? (ctx.state.fighters.find((f) => f.id === id) ?? null) : null;
}

/** Hrdina hráča — karty ako „Sinbad recovers 2 health" myslia jeho, nie hocikoho. */
export function heroFighterId(ctx: Ctx, player: PlayerIndex): FighterId | null {
  return heroOf(ctx.state, player)?.id ?? null;
}

/** Figúrky v priestoroch susediacich s daným priestorom. */
export function adjacentFighters(ctx: Ctx, spaceId: SpaceId): FighterId[] {
  const around = new Set(neighbors(getMap(ctx), spaceId));
  return ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id);
}

/** Prázdne priestory v zónach, kde figúrka stojí. */
export function emptySpacesInZone(ctx: Ctx, fighterId: FighterId): SpaceId[] {
  const fighter = fighterOrNull(ctx, fighterId);
  if (!fighter) return [];
  const occupied = new Set(ctx.state.fighters.map((f) => f.spaceId));
  return freeSpacesInZonesOf(getMap(ctx), occupied, fighter.spaceId);
}

/** Má figúrka vôbec kam ísť? Bez cieľa nemá zmysel pýtať sa. */
export function canMove(ctx: Ctx, fighterId: FighterId, max: number, through: Through): boolean {
  const fighter = fighterOrNull(ctx, fighterId);
  if (!fighter) return false;
  const paths = pathsWithin(getMap(ctx), ctx.state, fighter, max, through);
  return [...paths.keys()].some((spaceId) => spaceId !== fighter.spaceId);
}

/** „Presuň sa až o N priestorov" — voľba pre hráča. */
export function askMove(
  e: EffectCtx,
  options: {
    fighters: FighterId[];
    max: number;
    label: string;
    through?: Through;
    optional?: boolean;
    follow?: FollowUp;
  },
): void {
  const through = options.through ?? false;
  const usable = options.fighters.filter((id) => canMove(e.ctx, id, options.max, through));
  if (usable.length === 0) return;
  askChoice(e.ctx, {
    kind: 'move',
    player: e.player,
    source: e.cardId,
    label: options.label,
    optional: options.optional ?? true,
    fighters: usable,
    max: options.max,
    through,
    ...(options.follow ? { follow: options.follow } : {}),
  });
}

/**
 * „Tvoj súper" mimo súboja (pri pláne). V súboji je to druhý hráč súboja;
 * pri pláne v duele jediný súper, vo free-for-all najbližší súper v poradí.
 */
export function opponentOf(e: EffectCtx): PlayerIndex | null {
  if (e.opponentPlayer !== null) return e.opponentPlayer;
  const count = e.ctx.state.players.length;
  for (let step = 1; step < count; step++) {
    const index = (e.player + step) % count;
    const seat = e.ctx.state.players[index];
    if (seat && !seat.eliminated && areEnemies(e.ctx.state, index, e.player)) return index;
  }
  return null;
}

/** Súperove figúrky hráča. */
export function enemyFighters(ctx: Ctx, player: PlayerIndex): FighterId[] {
  return ctx.state.fighters.filter((f) => areEnemies(ctx.state, f.owner, player)).map((f) => f.id);
}

/**
 * Nazretie do súperovej ruky. Houdiniho „A Magician Never Reveals His
 * Secrets" to smie zrušiť — majiteľ kartu ukáže a efekt prepadne. Hráč by
 * to urobil vždy, preto to engine robí sám, bez otázky. Vráti, či sa ruka
 * naozaj ukázala.
 */
export function revealHand(e: EffectCtx, owner: PlayerIndex): boolean {
  const hand = e.ctx.state.players[owner]?.hand ?? [];
  const secret = hand.find(
    (id) => e.ctx.state.cards[id]?.defId === 'harry-houdini.a-magician-never-reveals-his-secrets',
  );
  if (secret) {
    emit(e.ctx, { type: 'handRevealed', player: e.player, owner, cards: [secret] });
    emit(e.ctx, { type: 'effectCancelled', player: e.player, cardId: e.cardId });
    return false;
  }
  emit(e.ctx, { type: 'handRevealed', player: e.player, owner, cards: hand.slice() });
  return true;
}

/**
 * Obkľúčenie: aspoň dve figúrky hráča `by` susedia s cieľom (poznámka
 * k balíčku Oda Nobunaga v zdroji).
 */
export function isFlanked(ctx: Ctx, targetId: FighterId | null, by: PlayerIndex): boolean {
  const target = fighterOrNull(ctx, targetId);
  if (!target) return false;
  const around = new Set(neighbors(getMap(ctx), target.spaceId));
  return ctx.state.fighters.filter((f) => f.owner === by && around.has(f.spaceId)).length >= 2;
}

/** Leží na priestore hmla (akéhokoľvek hráča)? */
export function hasFog(ctx: Ctx, spaceId: SpaceId): boolean {
  return tokensOf(ctx.state).some((t) => t.kind === 'fog' && t.spaceId === spaceId);
}

/** „You may BOOST this card" — voliteľná voľba karty z ruky (aj Vibranium Suit), ktorá sa odhodí za boost. */
export function askBoost(e: EffectCtx, label: string): void {
  if (boostSources(e.ctx, e.player).length === 0) return;
  askChoice(e.ctx, {
    kind: 'card',
    player: e.player,
    source: e.cardId,
    label,
    optional: true,
    from: 'hand',
    purpose: 'boost',
    side: e.role === 'defender' ? 'defender' : 'attacker',
  });
}

/** „Your opponent discards 1 card." — kartu vyberá súper sám. */
export function opponentDiscards(e: EffectCtx, player: PlayerIndex | null): void {
  if (player === null || (e.ctx.state.players[player]?.hand.length ?? 0) === 0) return;
  askChoice(e.ctx, {
    kind: 'card',
    player,
    source: e.cardId,
    label: 'Súperov efekt: odhoď 1 kartu z ruky',
    optional: false,
    from: 'hand',
    purpose: 'discard',
  });
}

/**
 * Zrušenie efektov karty v súboji cez reducer — ten vie, či sa zrušiť dá
 * (Brutal Strike, Ciri so 7+ Source). Hook, nie import: reduce.ts importuje
 * efekty a priamy import by vytvoril kruh.
 */
let cancelHook: ((ctx: Ctx, side: 'attacker' | 'defender') => void) | null = null;
export function setCancelHook(hook: (ctx: Ctx, side: 'attacker' | 'defender') => void): void {
  cancelHook = hook;
}
export function cancel(ctx: Ctx, side: 'attacker' | 'defender'): void {
  cancelHook?.(ctx, side);
}

/** Počítadlo hrdinu (Beowulfov Rage) — verejné. */
export function counter(ctx: Ctx, player: PlayerIndex, key: string): number {
  return ctx.state.players[player]?.counters?.[key] ?? 0;
}

/** Zmena počítadla s hranicami a udalosťou. Vráti skutočnú zmenu. */
export function addCounter(ctx: Ctx, player: PlayerIndex, key: string, delta: number, max = Infinity): number {
  const seat = ctx.state.players[player];
  if (!seat) return 0;
  const before = seat.counters?.[key] ?? 0;
  const after = Math.max(0, Math.min(max, before + delta));
  if (after === before) return 0;
  (seat.counters ??= {})[key] = after;
  emit(ctx, { type: 'counterChanged', player, key, value: after });
  return after - before;
}

/** Karty, ktorými smie hráč boostovať: ruka + Vibranium Suit (Black Panther). */
export function boostSources(ctx: Ctx, player: PlayerIndex): string[] {
  const seat = ctx.state.players[player];
  return [...(seat?.hand ?? []), ...(seat?.suit ?? [])];
}
